import { useEffect } from "react";

/**
 * Feed Beads feedback widget.
 *
 * Loads the widget script from the shared Feed Beads instance so the app
 * collects bug reports, ideas and feedback without anyone pasting a script tag
 * into index.html. `bun run setup:feedback` provisions the project and writes
 * the key; this component is what actually puts it on the page.
 *
 * NOT LOADED ON LOCALHOST, deliberately. A Feed Beads project owns a public
 * board and changelog, so a second "<app>-dev" project would mean a second
 * public roadmap nobody should see — the wrong shape for what is really just
 * "keep development noise out of the real inbox". Gating on the hostname gets
 * that for free: one project per app, and the inbox only ever hears from real
 * users. To exercise the widget itself locally, point a build at a throwaway
 * project key rather than reaching for a second one per app.
 *
 * The hostname is the gate rather than NODE_ENV because a production build run
 * locally (`bun run build && bun run start`) is still development — it is the
 * machine, not the bundle mode, that decides whether feedback is real.
 */

declare const __FEEDBACK_PROJECT_KEY__: string;
declare const __FEEDBACK_SCRIPT_URL__: string;

/** Hosts that are never a real deployment. */
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "[::1]", "::1"]);

/**
 * Build-time defines are absent when the bundler has not replaced them (dev
 * server, tests), where referencing the bare identifier would throw a
 * ReferenceError rather than yield undefined — hence the `typeof` guard.
 */
function define(value: string | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

const PROJECT_KEY = define(
  typeof __FEEDBACK_PROJECT_KEY__ === "string" ? __FEEDBACK_PROJECT_KEY__ : undefined,
);
const SCRIPT_URL = define(
  typeof __FEEDBACK_SCRIPT_URL__ === "string" ? __FEEDBACK_SCRIPT_URL__ : undefined,
);

/** Whether the widget should load in the current browser context. */
export function shouldLoadWidget(hostname: string, projectKey = PROJECT_KEY): boolean {
  if (!projectKey) return false;
  if (LOCAL_HOSTNAMES.has(hostname)) return false;
  // *.local and bare hostnames (a phone hitting the dev server over the LAN)
  // are development too.
  if (hostname.endsWith(".local") || !hostname.includes(".")) return false;
  return true;
}

export function FeedbackWidget() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!shouldLoadWidget(window.location.hostname)) return;

    const scriptUrl = SCRIPT_URL || "https://feed-beads.holymolylab.com/widget.js";

    // Re-mounting must not stack widgets — StrictMode runs effects twice in
    // development, and the router can remount the tree.
    const existing = document.querySelector<HTMLScriptElement>("script[data-feed-beads]");
    if (existing) return;

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset.feedBeads = PROJECT_KEY;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}
